class GVFOrderCart {
  constructor() {
    this.sidebar = document.querySelector('[data-gvf-cart-sidebar]');
    this.currency = window.Shopify?.currency?.active || 'USD';
    this.locationKey = 'gvf:last-order-location';
    this.restoreKey = 'gvf:return-order-location';
    this.productVariants = new WeakMap();
    document.addEventListener('click', (event) => this.handleClick(event));
    document.addEventListener('change', (event) => this.handleProductOptionChange(event));
    document.addEventListener('submit', (event) => this.handleProductSubmit(event));
    document.querySelectorAll('.gvf-product-form').forEach((form) => this.updateProductVariant(form));
    this.configureReturnLinks();
    this.configureCartContinueLinks();
    this.restorePagePosition();
    window.addEventListener('pageshow', () => this.restorePagePosition());
    if (this.sidebar) {
      this.rememberCurrentOrderSurface();
      this.refreshCart();
      window.addEventListener('pageshow', () => this.refreshCart());
    }
  }

  route(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${path.replace(/^\/+/, '')}`;
  }

  money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') return window.Shopify.formatMoney(cents);
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format((Number(cents) || 0) / 100);
  }

  async jsonFetch(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: { Accept: 'application/json', ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }), ...(options.headers || {}) }
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Unable to update your cart.');
    return result;
  }

  loadCart() {
    return this.jsonFetch(this.route('cart.js'));
  }

  refreshCart() {
    return this.loadCart().then((cart) => this.render(cart)).catch(() => {});
  }

  safeStorefrontLocation(value) {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      const path = url.pathname.toLowerCase();
      if (path.startsWith('/admin') || path.startsWith('/checkouts') || path.startsWith('/checkout')) return null;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_error) {
      return null;
    }
  }

  isStorefrontProductLink(link) {
    if (!link?.href) return false;
    try {
      const url = new URL(link.href, window.location.href);
      return url.origin === window.location.origin && url.pathname.toLowerCase().includes('/products/');
    } catch (_error) {
      return false;
    }
  }

  storedLocation(key = this.locationKey) {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      if (!stored || Date.now() - Number(stored.savedAt || 0) > 2 * 60 * 60 * 1000) return null;
      const path = this.safeStorefrontLocation(stored.path);
      return path ? { ...stored, path } : null;
    } catch (_error) {
      return null;
    }
  }

  saveLocation(key, stored) {
    try { window.sessionStorage.setItem(key, JSON.stringify(stored)); } catch (_error) {}
  }

  rememberCurrentOrderSurface(includeRestore = false) {
    const path = this.safeStorefrontLocation(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    if (!path) return;
    const stored = { path, scrollY: window.scrollY, savedAt: Date.now() };
    this.saveLocation(this.locationKey, stored);
    if (includeRestore) this.saveLocation(this.restoreKey, stored);
  }

  rememberOrderLocation(link) {
    if (!this.isStorefrontProductLink(link)) return;
    const path = this.safeStorefrontLocation(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    if (!path) return;
    const stored = { path, scrollY: window.scrollY, savedAt: Date.now() };
    this.saveLocation(this.locationKey, stored);
    this.saveLocation(this.restoreKey, stored);

    try {
      const target = new URL(link.href, window.location.href);
      target.searchParams.set('return_to', path);
      link.href = target.toString();
    } catch (_error) {}
  }

  returnLocation() {
    const requested = this.safeStorefrontLocation(new URLSearchParams(window.location.search).get('return_to'));
    if (requested) return requested;

    try {
      const referrer = new URL(document.referrer);
      const previous = this.safeStorefrontLocation(referrer.href);
      if (previous) return previous;
    } catch (_error) {
      // Fall through to the saved collection location.
    }

    const stored = this.storedLocation();
    return stored?.path || null;
  }

  configureReturnLinks() {
    const links = document.querySelectorAll('[data-gvf-product-return]');
    if (!links.length) return;
    const destination = this.returnLocation();
    if (!destination) return;

    links.forEach((link) => {
      link.href = destination;
      link.addEventListener('click', (event) => {
        let referrer = null;
        try { referrer = this.safeStorefrontLocation(document.referrer); } catch (_error) {}
        if (referrer && referrer === destination && window.history.length > 1) {
          event.preventDefault();
          window.history.back();
        }
      });
    });
  }

  configureCartContinueLinks() {
    const links = document.querySelectorAll('[data-gvf-cart-continue]');
    if (!links.length) return;
    const stored = this.storedLocation();
    if (!stored) return;
    links.forEach((link) => { link.href = stored.path; });
  }

  restorePagePosition() {
    const stored = this.storedLocation(this.restoreKey);
    if (!stored) return;
    const current = this.safeStorefrontLocation(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    if (stored.path !== current || !Number.isFinite(Number(stored.scrollY))) return;

    const restore = () => window.scrollTo({ top: Number(stored.scrollY), behavior: 'auto' });
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(restore);
      window.setTimeout(restore, 180);
    });
    try { window.sessionStorage.removeItem(this.restoreKey); } catch (_error) {}
  }

  setBusy(isBusy) {
    if (this.sidebar) this.sidebar.setAttribute('aria-busy', String(isBusy));
  }

  status(message) {
    const target = this.sidebar?.querySelector('[data-gvf-cart-status]');
    if (target) target.textContent = message;
  }

  async handleClick(event) {
    const productLink = event.target.closest('a[href]');
    if (this.isStorefrontProductLink(productLink)) this.rememberOrderLocation(productLink);

    const cartLink = event.target.closest('[data-gvf-cart-open]');
    if (cartLink) this.rememberCurrentOrderSurface(true);

    const checkoutLink = event.target.closest('[data-gvf-checkout]');
    if (checkoutLink && this.sidebar?.contains(checkoutLink)) {
      event.preventDefault();
      await this.openCartForCheckout(checkoutLink);
      return;
    }

    const addButton = event.target.closest('[data-gvf-order-add]');
    if (addButton) {
      event.preventDefault();
      await this.addFromCard(addButton);
      return;
    }

    const actionButton = event.target.closest('[data-gvf-cart-action]');
    if (!actionButton || !this.sidebar?.contains(actionButton)) return;
    event.preventDefault();
    const item = actionButton.closest('[data-cart-key]');
    if (!item) return;
    const quantity = Number(item.dataset.cartQuantity || 0);
    const action = actionButton.dataset.gvfCartAction;
    const nextQuantity = action === 'remove' ? 0 : action === 'increase' ? quantity + 1 : Math.max(0, quantity - 1);
    await this.change(item.dataset.cartKey, nextQuantity);
  }

  async openCartForCheckout(link) {
    const original = link.textContent;
    link.setAttribute('aria-disabled', 'true');
    link.textContent = 'Opening cart…';
    this.setBusy(true);
    this.status('Opening order details…');
    try {
      if (this.sidebar?.dataset.gvfOrderChannel === 'takeout') {
        await this.jsonFetch(this.route('cart/update.js'), {
          method: 'POST',
          body: JSON.stringify({ attributes: { 'Fulfillment method': 'Store Pickup' } })
        });
      }
      window.location.assign(this.route('cart'));
    } catch (error) {
      this.status(error.message);
      link.removeAttribute('aria-disabled');
      link.textContent = original;
      this.setBusy(false);
    }
  }

  async addFromCard(button) {
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    this.setBusy(true);
    try {
      await this.jsonFetch(this.route('cart/add.js'), {
        method: 'POST',
        body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] })
      });
      const cart = await this.loadCart();
      this.render(cart);
      this.status(`${button.dataset.productTitle || 'Item'} added to your cart.`);
      button.textContent = 'Added';
      window.setTimeout(() => { button.textContent = original; }, 1400);
    } catch (error) {
      this.status(error.message);
      button.textContent = original;
    } finally {
      button.disabled = false;
      this.setBusy(false);
    }
  }

  async change(key, quantity) {
    this.setBusy(true);
    this.status(quantity === 0 ? 'Removing item…' : 'Updating quantity…');
    try {
      const cart = await this.jsonFetch(this.route('cart/change.js'), {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity })
      });
      this.render(cart);
      this.status(quantity === 0 ? 'Item removed.' : 'Quantity updated.');
    } catch (error) {
      this.status(error.message);
    } finally {
      this.setBusy(false);
    }
  }

  variantsFor(form) {
    if (this.productVariants.has(form)) return this.productVariants.get(form);
    const source = form.querySelector('[data-gvf-product-variants]');
    if (!source) return [];
    try {
      const variants = JSON.parse(source.textContent);
      this.productVariants.set(form, variants);
      return variants;
    } catch (_error) {
      return [];
    }
  }

  selectedProductOptions(form) {
    return Array.from(form.querySelectorAll('[data-gvf-product-option]'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((select) => select.value);
  }

  updateProductVariant(form) {
    const selectors = form.querySelectorAll('[data-gvf-product-option]');
    const idInput = form.querySelector('[data-gvf-product-variant-id]');
    const button = form.querySelector('[type="submit"]');
    if (!selectors.length || !idInput || !button) return { available: !button.disabled, id: idInput?.value };

    const selectedOptions = this.selectedProductOptions(form);
    const variant = this.variantsFor(form).find((candidate) =>
      candidate.options.length === selectedOptions.length
      && candidate.options.every((value, index) => value === selectedOptions[index])
    );
    const available = Boolean(variant && (variant.available || variant.inventory_policy === 'continue'));
    const status = form.querySelector('[data-gvf-product-form-status]');

    idInput.value = variant?.id || '';
    button.disabled = !available;
    button.textContent = available ? button.dataset.gvfProductSubmitLabel : 'Sold out';
    if (status) status.textContent = variant && !available ? 'This combination is sold out.' : variant ? '' : 'This combination is unavailable.';
    if (variant?.price != null) {
      document.querySelectorAll('.gvf-product-hero__price').forEach((price) => { price.textContent = this.money(variant.price); });
    }
    return variant;
  }

  handleProductOptionChange(event) {
    const selector = event.target.closest('[data-gvf-product-option]');
    if (!selector) return;
    const form = selector.closest('.gvf-product-form');
    if (form) this.updateProductVariant(form);
  }

  async handleProductSubmit(event) {
    const form = event.target.closest('[data-gvf-product-order-form="true"]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-gvf-product-form-status]');
    const variant = this.updateProductVariant(form);
    if (!variant || (!variant.available && variant.inventory_policy !== 'continue')) {
      status.textContent = variant ? 'This combination is sold out.' : 'Please choose an available combination.';
      return;
    }
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    status.textContent = '';
    try {
      await this.jsonFetch(this.route('cart/add.js'), { method: 'POST', body: new FormData(form) });
      const cart = await this.loadCart();
      this.updateCartBubble(cart.item_count);
      status.textContent = 'Added to your cart. Continue shopping or view your cart when you are ready.';
      button.textContent = 'Added';
      window.setTimeout(() => { button.textContent = original; }, 1400);
    } catch (error) {
      status.textContent = error.message;
      button.textContent = original;
    } finally {
      button.disabled = false;
    }
  }

  updateCartBubble(count) {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return;
    const visible = bubble.querySelector('span[aria-hidden="true"]');
    const accessible = bubble.querySelector('.visually-hidden');
    if (visible) visible.textContent = count;
    if (accessible) accessible.textContent = `${count} items`;
  }

  render(cart) {
    if (!this.sidebar) return;
    this.currency = cart.currency || this.currency;
    const itemsHost = this.sidebar.querySelector('[data-gvf-cart-items]');
    const empty = this.sidebar.querySelector('[data-gvf-cart-empty]');
    const footer = this.sidebar.querySelector('[data-gvf-cart-footer]');
    const count = this.sidebar.querySelector('[data-gvf-cart-count]');
    const subtotal = this.sidebar.querySelector('[data-gvf-cart-subtotal]');
    count.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
    empty.hidden = cart.item_count > 0;
    itemsHost.hidden = cart.item_count === 0;
    footer.hidden = cart.item_count === 0;
    subtotal.textContent = this.money(cart.total_price);
    itemsHost.replaceChildren(...cart.items.map((item) => this.buildItem(item)));
    this.updateCartBubble(cart.item_count);
  }

  buildItem(item) {
    const article = document.createElement('article');
    article.className = 'gvf-cart-sidebar__item';
    article.setAttribute('role', 'listitem');
    article.dataset.cartKey = item.key;
    article.dataset.cartQuantity = item.quantity;

    if (item.featured_image?.url || item.image) {
      const image = document.createElement('img');
      image.className = 'gvf-cart-sidebar__image';
      image.src = item.featured_image?.url || item.image;
      image.alt = '';
      image.width = 124;
      image.height = 124;
      article.append(image);
    }

    const copy = document.createElement('div');
    copy.className = 'gvf-cart-sidebar__item-copy';
    const head = document.createElement('div');
    head.className = 'gvf-cart-sidebar__item-head';
    const title = document.createElement('h3');
    title.className = 'gvf-cart-sidebar__item-title';
    const titleLink = document.createElement('a');
    titleLink.href = item.url;
    titleLink.textContent = item.product_title || item.title;
    title.append(titleLink);
    const price = document.createElement('span');
    price.className = 'gvf-cart-sidebar__item-price';
    price.textContent = this.money(item.final_line_price);
    head.append(title, price);
    copy.append(head);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('p');
      variant.className = 'gvf-cart-sidebar__variant';
      variant.textContent = item.variant_title;
      copy.append(variant);
    }

    const actions = document.createElement('div');
    actions.className = 'gvf-cart-sidebar__item-actions';
    const quantity = document.createElement('div');
    quantity.className = 'gvf-cart-sidebar__quantity';
    quantity.setAttribute('aria-label', `Quantity for ${item.product_title || item.title}`);
    const decrease = this.actionButton('decrease', '−', `Decrease ${item.product_title || item.title} quantity`);
    const value = document.createElement('span');
    value.textContent = item.quantity;
    value.setAttribute('aria-live', 'polite');
    const increase = this.actionButton('increase', '+', `Increase ${item.product_title || item.title} quantity`);
    quantity.append(decrease, value, increase);
    const remove = this.actionButton('remove', 'Remove', `Remove ${item.product_title || item.title}`);
    remove.className = 'gvf-cart-sidebar__remove';
    actions.append(quantity, remove);
    copy.append(actions);
    article.append(copy);
    return article;
  }

  actionButton(action, label, ariaLabel) {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.gvfCartAction = action;
    button.textContent = label;
    button.setAttribute('aria-label', ariaLabel);
    return button;
  }
}

document.addEventListener('DOMContentLoaded', () => new GVFOrderCart());
